import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import {
  useLazyGetIndustriesQuery,
  useCreateBusinessMutation,
  useUpdateBusinessMutation,
  useLazyGetBusinessByItsQuery,
  useSubmitContactFormMutation,
} from "../api/businessApi";

const SubmitBusiness = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [getIndustries, { data: industriesData }] = useLazyGetIndustriesQuery();
  const [getBusinessByIts, { data: businessData }] = useLazyGetBusinessByItsQuery();
  const [createBusiness, { isLoading: isCreating }] = useCreateBusinessMutation();
  const [updateBusiness, { isLoading: isUpdating }] = useUpdateBusinessMutation();
  const [submitContactForm] = useSubmitContactFormMutation();
  
  const [existingBusiness, setExistingBusiness] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    its_number: "",
    name: "",
    contact: "",
    qualification: "",
    occupation: "",
    industry: "",
    designation: "",
    company_name: "",
    linkedin: "",
    website: "",
    other_service: "",
    description: "",
    logo: "",
    badges: [],
  });

  const [badgeInput, setBadgeInput] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    professionalInfo: true,
    onlinePresence: false,
    services: false,
    badges: false,
  });

  const occupationOptions = [
    { value: "Business", label: "Business" },
    { value: "Professional", label: "Professional" },
  ];

  useEffect(() => {
    getIndustries();
    if (user?.its) {
      getBusinessByIts(user.its);
    }
  }, [getIndustries, getBusinessByIts, user]);

  useEffect(() => {
    if (businessData && user?.its) {
      setExistingBusiness(businessData);
      setIsEditMode(true);
      setFormData({
        email: businessData.email || "",
        its_number: businessData.its_number || "",
        name: businessData.name || "",
        contact: businessData.contact || "",
        qualification: businessData.qualification || "",
        occupation: businessData.occupation || "",
        industry: businessData.industry || "",
        designation: businessData.designation || "",
        company_name: businessData.company_name || "",
        linkedin: businessData.linkedin || "",
        website: businessData.website || "",
        other_service: businessData.other_service || "",
        description: businessData.description || "",
        logo: businessData.logo || "",
        badges: businessData.badges || [],
      });
    }
  }, [businessData, user]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        logo: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear previous error
    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Email validation
    if (name === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setValidationErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email address",
        }));
      }
    }

    // ITS Number validation
    if (name === "its_number" && value) {
      const itsNumber = parseInt(value, 10);
      if (isNaN(itsNumber) || itsNumber <= 0) {
        setValidationErrors((prev) => ({
          ...prev,
          its_number: "ITS Number must be a valid positive number",
        }));
      }
    }
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleAddBadge = () => {
    if (badgeInput.trim() && !formData.badges.includes(badgeInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        badges: [...prev.badges, badgeInput.trim()],
      }));
      setBadgeInput("");
    }
  };

  const handleRemoveBadge = (index) => {
    setFormData((prev) => ({
      ...prev,
      badges: prev.badges.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    const newValidationErrors = {};
    const requiredFields = [
      "email",
      "its_number",
      "name",
      "qualification",
      "occupation",
      "industry",
      "designation",
      "company_name",
      "contact",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newValidationErrors[field] = `${field
          .replace("_", " ")
          .replace(/^\w/, (c) => c.toUpperCase())} is required`;
      }
    });

    // Email format validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newValidationErrors.email = "Please enter a valid email address";
    }

    // ITS Number validation
    if (formData.its_number) {
      const itsNumber = parseInt(formData.its_number, 10);
      if (isNaN(itsNumber) || itsNumber <= 0) {
        newValidationErrors.its_number =
          "ITS Number must be a valid positive number";
      }
    }

    setValidationErrors(newValidationErrors);

    if (Object.keys(newValidationErrors).length === 0) {
      try {
        // Submit business with pending status
        const submissionData = {
          ...formData,
          timestamp: new Date().toISOString(),
          status: "pending",
          sponsored: false,
          level2_sponsored: false,
          internship: false,
        };

        if (isEditMode && existingBusiness) {
          // Update existing business
          await updateBusiness({
            id: existingBusiness.id,
            ...formData,
            timestamp: new Date().toISOString(),
          }).unwrap();
          
          Swal.fire({
            title: "Success!",
            text: "Your business information has been updated successfully.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          
          navigate('/business-directory');
        } else {
          // Create new business
          const res = await createBusiness(submissionData).unwrap();
          if (res) {
            // Send email notification using contact form API
            await submitContactForm({
              name: formData.name,
              email: formData.email,
              subject: "New Business Directory Submission",
              message: `New business submission from ${formData.name}:
        
Company: ${formData.company_name}
Industry: ${formData.industry}
Designation: ${formData.designation}
Contact: ${formData.contact}
ITS Number: ${formData.its_number}
Qualification: ${formData.qualification}
Occupation: ${formData.occupation}
LinkedIn: ${formData.linkedin}
Website: ${formData.website}
Other Services: ${formData.other_service}
Description: ${formData.description}
Badges: ${formData.badges.join(", ")}

Please review and approve this submission for the business directory.`,
              type: "business_submission",
            }).unwrap();
            
            Swal.fire({
              title: "Success!",
              text: "Your business submission has been sent for review. You'll be notified once it's approved.",
              icon: "success",
              confirmButtonColor: "#135879",
            });

            // Reset form
            setFormData({
              email: "",
              its_number: "",
              name: "",
              contact: "",
              qualification: "",
              occupation: "",
              industry: "",
              designation: "",
              company_name: "",
              linkedin: "",
              website: "",
              other_service: "",
              description: "",
              logo: "",
              badges: [],
            });
            navigate("/");
          } else {
            Swal.fire({
              title: "Error!",
              text: "Failed to submit business. Please try again.",
              icon: "error",
              confirmButtonColor: "#dc2626",
            });
          }
        }
      } catch (error) {
        console.error("Error submitting business:", error);
        Swal.fire({
          title: "Error!",
          text:
            error.message ||
            error.data ||
            "Failed to submit business. Please try again.",
          icon: "error",
          confirmButtonColor: "#dc2626",
        });
      }
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="bg-[#135879] px-6 py-4">
            <h1 className="text-2xl font-bold text-white">
              {isEditMode ? "Update Your Business" : "Submit Your Business"}
            </h1>
            <p className="text-[#d4af37] mt-2">
              {isEditMode 
                ? "Update your business information in the directory."
                : "Join the Chicago Jamaat Business Directory. Your submission will be reviewed before being published."
              }
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleSection("basicInfo")}
              >
                <h4 className="text-lg font-medium text-gray-900">
                  Basic Information
                </h4>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${
                    expandedSections.basicInfo ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {expandedSections.basicInfo && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="your.email@example.com"
                    />
                    {validationErrors.email && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.email}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      ITS Number *
                    </label>
                    <input
                      type="number"
                      name="its_number"
                      value={formData.its_number}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="123456"
                    />
                    {validationErrors.its_number && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.its_number}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="Your full name"
                    />
                    {validationErrors.name && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.name}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Contact Number *
                    </label>
                    <input
                      type="tel"
                      name="contact"
                      value={formData.contact}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="+1 (555) 123-4567"
                    />
                    {validationErrors.contact && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.contact}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Professional Information Section */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleSection("professionalInfo")}
              >
                <h4 className="text-lg font-medium text-gray-900">
                  Professional Information
                </h4>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${
                    expandedSections.professionalInfo ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {expandedSections.professionalInfo && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Qualification *
                    </label>
                    <textarea
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleInputChange}
                      rows={2}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="Your educational background and qualifications"
                    />
                    {validationErrors.qualification && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.qualification}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Type *
                    </label>
                    <Select
                      name="occupation"
                      value={occupationOptions.find(
                        (opt) => opt.value === formData.occupation
                      )}
                      onChange={handleSelectChange}
                      options={occupationOptions}
                      className="mt-1"
                      classNamePrefix="select"
                      placeholder="Select type..."
                      isClearable
                    />
                    {validationErrors.occupation && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.occupation}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Industry *
                    </label>
                    <CreatableSelect
                      name="industry"
                      value={
                        formData.industry
                          ? {
                              value: formData.industry,
                              label: formData.industry,
                            }
                          : null
                      }
                      onChange={(selectedOption) => {
                        setFormData((prev) => ({
                          ...prev,
                          industry: selectedOption ? selectedOption.value : "",
                        }));
                      }}
                      options={
                        industriesData?.industries?.map((industry) => ({
                          value: industry,
                          label: industry,
                        })) || []
                      }
                      className="mt-1"
                      classNamePrefix="select"
                      placeholder="Select or add industry..."
                      isClearable
                      formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                    />
                    {validationErrors.industry && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.industry}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Designation *
                    </label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="Your job title or position"
                    />
                    {validationErrors.designation && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.designation}
                      </span>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="Your company or business name"
                    />
                    {validationErrors.company_name && (
                      <span className="text-red-500 text-sm">
                        {validationErrors.company_name}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Company Logo Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Company Logo (Optional)
              </h4>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex justify-center sm:justify-start">
                  {formData.logo ? (
                    <div className="w-24 h-24 flex items-center justify-center">
                      <img
                        src={formData.logo}
                        alt="Company logo"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                      <svg
                        className="h-8 w-8 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 items-center">
                  <label className="cursor-pointer">
                    <span className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#135879]">
                      Upload Logo
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleImageUpload}
                    />
                  </label>
                  {formData.logo && (
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, logo: "" }))
                      }
                      className="text-sm text-red-600 hover:text-red-800 px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Online Presence Section */}
            <div className="bg-green-50 p-4 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleSection("onlinePresence")}
              >
                <h4 className="text-lg font-medium text-gray-900">
                  Online Presence (Optional)
                </h4>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${
                    expandedSections.onlinePresence ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {expandedSections.onlinePresence && (
                <div className="mt-4 grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="https://linkedin.com/in/username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="https://yourcompany.com"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Services Section */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleSection("services")}
              >
                <h4 className="text-lg font-medium text-gray-900">
                  Services & Description (Optional)
                </h4>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${
                    expandedSections.services ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {expandedSections.services && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Other Services
                    </label>
                    <textarea
                      name="other_service"
                      value={formData.other_service}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="Describe additional services you offer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Business Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      placeholder="Describe your business, what you do, and what makes you unique"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Badges Section */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleSection("badges")}
              >
                <h4 className="text-lg font-medium text-gray-900">
                  Skills & Specialties (Optional)
                </h4>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${
                    expandedSections.badges ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {expandedSections.badges && (
                <div className="mt-4 space-y-4">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={badgeInput}
                      onChange={(e) => setBadgeInput(e.target.value)}
                      placeholder="Enter a skill or specialty"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#135879] focus:border-[#135879]"
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddBadge())
                      }
                    />
                    <button
                      type="button"
                      onClick={handleAddBadge}
                      className="px-4 py-2 bg-[#135879] text-white rounded-md hover:bg-[#0e4562] focus:outline-none focus:ring-2 focus:ring-[#135879] whitespace-nowrap"
                    >
                      Add
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {formData.badges.map((badge, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#135879] text-white"
                      >
                        {badge}
                        <button
                          type="button"
                          onClick={() => handleRemoveBadge(index)}
                          className="ml-2 text-[#d4af37] hover:text-white"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="bg-white border-t border-gray-200 pt-6">
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#135879]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isCreating ||
                    isUpdating ||
                    isSubmitting ||
                    Object.values(validationErrors).some((error) => error)
                  }
                  className="w-full sm:w-auto px-6 py-3 bg-[#135879] text-white rounded-md text-sm font-medium hover:bg-[#0e4562] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#135879]"
                >
                  {isCreating || isUpdating || isSubmitting
                    ? (isEditMode ? "Updating..." : "Submitting...")
                    : (isEditMode ? "Update Business" : "Submit for Review")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmitBusiness;
